import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import { tripPlansCreateNewTripClicked } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import Clickable from 'components/Common/Clickable/Clickable'
import IconButton from 'components/Luxkit/Button/IconButton'
import TextButton from 'components/Luxkit/Button/TextButton'
import LinePlusCircleIcon from 'components/Luxkit/Icons/line/LinePlusCircleIcon'
import LinePlusIcon from 'components/Luxkit/Icons/line/LinePlusIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import CreateTrip from 'tripPlanner/components/TripModal/Trips/CreateTrip/CreateTripTP'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { FullTrip } from 'tripPlanner/types/common'

const Panel = styled(Clickable)`
  position: relative;
  display: grid;
  grid-template-rows: 100%;
  grid-template-columns: ${rem(80)} 1fr;
  width: 100%;
  height: 100%;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    width: ${rem(364)};
    grid-template-columns: 100%;
    border: 1px solid ${({ theme }) => theme.palette.neutral.six};
    border-radius: ${(props) => props.theme.borderRadius.S};
    padding: ${rem(40)} ${rem(32)};
  }
`

const LabelWrapper = styled(CSSBreakpoint)`
  align-self: center;
`

const AddButton = styled(IconButton)`
  width: ${rem(80)};
  height: ${rem(80)};

  border: 1px solid ${({ theme }) => theme.palette.neutral.six};
  border-radius: ${(props) => props.theme.borderRadius.S};
`

interface Props {
  onCreateTrip?: (trip: FullTrip) => void
}

function CreateNewTripTile({ onCreateTrip }: Props) {
  const openModal = useOpenModal()

  const createNewTrip = useCallback(() => {
    fireInteractionEvent(tripPlansCreateNewTripClicked('trip_plans'))
    openModal(CreateTrip, {
      startDate: undefined,
      endDate: undefined,
      onCreateTrip,
    })
  }, [openModal, onCreateTrip])

  return (
    <Panel onClick={createNewTrip}>
      <CSSBreakpoint max="mobile">
        <AddButton kind="primary" variant="ghost" size="large" nonInteractable>
          <LinePlusIcon />
        </AddButton>
      </CSSBreakpoint>
      <LabelWrapper max="mobile">
        <BodyText variant="large">New trip plan</BodyText>
      </LabelWrapper>
      <CSSBreakpoint min="tablet">
        <Group
          direction="vertical"
          gap={20}
          horizontalAlign="start"
          verticalAlign="center"
          fullHeight
        >
          <Group direction="vertical" gap={12}>
            <Heading variant="heading4">Start building your next trip</Heading>
            <BodyText variant="medium">
              Save offers, explore destinations, create an itinerary, and
              arrange every aspect of your escape.
            </BodyText>
          </Group>
          <TextButton
            kind="primary"
            startIcon={<LinePlusCircleIcon />}
            data-testid="create-new-trip-button"
            nonInteractable
          >
            New trip plan
          </TextButton>
        </Group>
      </CSSBreakpoint>
    </Panel>
  )
}

export default CreateNewTripTile
